import React, { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { getDefaultValueIndex } from './util';
import { ISelect, ISelectOptions } from './types';
import styles from './styles.module.scss';

const Single = ({ className, defaultValue, dropupAuto, error, handleChange = () => {}, id, keyType = 'text', options, ...rest }: ISelect) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(defaultValue || options[0]?.value);
  const [targetId, setTargetId] = useState(getDefaultValueIndex({ defaultValue, options }));
  const divRef = useRef<HTMLDivElement>(null);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  const selectedText = useMemo(() => options.find(({ value }) => value === selectedOption.toString())?.text, [selectedOption]);

  useEffect(() => {
    if (options?.length) {
      const select = options.find(opt => opt.value === defaultValue)?.value;
      setSelectedOption(select ?? '');
    }
  }, [defaultValue]);

  const handleSelect = ({ value }: ISelectOptions, index: number) => {
    handleChange(value);
    setSelectedOption(value);
    setIsOpen(false);
    setTargetId(index);
  };

  const closeMenu = ({ target }: MouseEvent | FocusEvent) => {
    if (!divRef.current?.contains(target as HTMLElement)) {
      setIsOpen(false);
    }
  };

  // bluring the focus or clicking outside of the component
  // should close the select dropdown
  useEffect(() => {
    document.addEventListener('focusin', closeMenu);
    document.addEventListener('click', closeMenu);

    return () => {
      document.removeEventListener('focusin', closeMenu);
      document.removeEventListener('click', closeMenu);
    };
  }, []);

  const handleKeydown = (event: KeyboardEvent<HTMLDivElement>) => {
    const { code } = event;

    // allow default tab functionality
    if (code === 'Tab') {
      return true;
    }

    // to keep the browser from scrolling if using the arrow keys
    event.preventDefault();
    event.stopPropagation();

    if (code === 'ArrowDown') {
      // if closed, open the dropdown and select the first option
      if (!isOpen) {
        setIsOpen(true);
        setTargetId(0);
        // if open, move the selected option down one
      } else if (targetId + 1 <= options.length - 1) {
        setTargetId(targetId + 1);
      }
    }

    if (code === 'ArrowUp') {
      // if closed, open the dropdown and select the last option
      if (!isOpen) {
        setIsOpen(true);
        setTargetId(options.length - 1);
        // if open, move the selected option up one
      } else if (targetId > 0) {
        setTargetId(targetId - 1);
      }
    }

    if (code === 'End') {
      setTargetId(options.length - 1);
    }

    if (code === 'Enter' || code === 'NumpadEnter' || code === ' ') {
      if (!isOpen) {
        setIsOpen(true);
      } else {
        handleSelect(options[targetId], targetId);
      }
    }

    if (code === 'Escape') {
      setIsOpen(false);
    }

    if (code === 'Home') {
      setTargetId(0);
    }

    return null;
  };

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [targetId]);

  return (
    <div className={`${styles.combo} ${className || ''}`.trim()} ref={divRef} {...rest}>
      <div
        aria-activedescendant={`combo1-${targetId}`}
        aria-controls="listbox1"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={id}
        className={`${styles.input} d-inline-flex justify-content-between align-items-center form-control dropdown-toggle text-right ${error ? 'border-danger' : ''}`}
        data-testid="select"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeydown}
        role="combobox"
        tabIndex={0}
        {...{ id }}
      >
        <div className="filter-option overflow-hidden">
          <div className="filter-option-inner">
            <div className="filter-option-inner-inner user-select-none">{selectedText}</div>
          </div>
        </div>
      </div>
      <div className={`${styles.comboMenu} ${isOpen ? 'd-block' : 'd-none'} ${dropupAuto ? styles.dropupAuto : ''} dropdown-menu`} id="listbox1" role="listbox" tabIndex={-1}>
        {options.map(({ text, value }, index) => {
          const isSelected = index === targetId;

          return (
            <div
              aria-selected={isSelected}
              className={`dropdown-item ${isSelected ? 'active' : ''}`}
              data-testid="select-option"
              id={`combo1-${index}`}
              key={keyType === 'text' ? `${index}-${text}` : `${index}-${value}`}
              onClick={() => handleSelect({ text, value }, index)}
              onKeyDown={({ key }) => {
                if (key === 'Enter' || key === ' ') handleSelect({ text, value }, index);
              }}
              ref={isSelected ? activeOptionRef : null}
              role="option"
              tabIndex={0}
            >
              <span className="d-inline-block py-2">{text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { Single };
